import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Database, Building2, Users, Home, GitCompareArrows, BellRing,
  Calculator, FileText, LogOut, Shield, CalendarClock, Settings2, PhoneCall
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import UnifiedSearch from "@/components/UnifiedSearch";

const nav = [
  { to: "/dashboard", label: "Command Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/programs", label: "Programs Intelligence", icon: Database, testid: "nav-programs" },
  { to: "/agencies", label: "Agencies & Partners", icon: Building2, testid: "nav-agencies" },
  { to: "/contacts", label: "Contacts", icon: Users, testid: "nav-contacts" },
  { to: "/properties", label: "Vetted Properties", icon: Home, testid: "nav-properties" },
  { to: "/match", label: "Matching Engine", icon: GitCompareArrows, testid: "nav-match" },
  { to: "/underwriting", label: "Underwriting", icon: Calculator, testid: "nav-underwriting" },
  { to: "/scenarios", label: "Scenarios", icon: GitCompareArrows, testid: "nav-scenarios" },
  { to: "/funding", label: "Funding Calendar", icon: CalendarClock, testid: "nav-funding" },
  { to: "/access-requests", label: "Access Requests", icon: Users, testid: "nav-access-requests" },
  { to: "/leads", label: "Owner Leads", icon: BellRing, testid: "nav-leads", adminOnly: true },
  { to: "/packets", label: "Information Packets", icon: FileText, testid: "nav-packets" },
  { to: "/admin/data-inventory", label: "Data Inventory", icon: Settings2, testid: "nav-admin-inventory", adminOnly: true },
  { to: "/admin/alternative-uses", label: "Alt-Use Call Log", icon: PhoneCall, testid: "nav-admin-alt-uses", adminOnly: true },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#F8FAF9] text-slate-900 flex">
      <aside className="w-64 shrink-0 bg-white border-r border-slate-200 flex flex-col" data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-200">
          <Brand variant="dark" size={22} subtitle />
        </div>
        <nav className="flex-1 px-3 py-4 space-y-0.5">
          {nav.filter(i => !i.adminOnly || user?.role === "admin").map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-2.5 px-3 py-2 rounded-md text-sm ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-700 hover:bg-slate-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-2 px-2 py-2">
            <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
              {user?.name?.[0] || user?.email?.[0] || "?"}
            </div>
            <div className="min-w-0">
              <div className="text-xs font-semibold truncate">{user?.name || user?.email}</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1">
                <Shield className="w-3 h-3" /> {user?.role || "user"}
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" className="w-full mt-2" data-testid="logout-btn"
                  onClick={async () => { await logout(); navigate("/login"); }}>
            <LogOut className="w-3.5 h-3.5 mr-1" /> Sign out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0">
        <div className="border-b border-slate-200 bg-white px-6 py-3 flex items-center justify-between gap-4">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Texas Metros · San Antonio · Austin · Houston · Dallas–Fort Worth</div>
          <UnifiedSearch />
        </div>
        <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
}
